import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import OurLayouts from "../../layout";

const API_BASE = process.env.REACT_APP_API_URL;

const VITAL_LABELS = {
  temperature: "Temperature (°F)",
  bloodPressure: "Blood Pressure (mmHg)",
  oxygenLevel: "oxygenLevel (%)",
  respirationRate: "Respiration Rate (/min)",
  heartRate: "Heart Rate (bpm)",
  height: "Height",
  weight: "Weight (kg)",
  bloodGlucose: "Blood Glucose (mg/dL)",
};

export default function ViewVital() {
  const { state } = useLocation();
  const type = state?.type; // e.g. "temperature" or "bloodPressure"
  const patientCode = state?.patientCode;
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Tooltip state
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    content: "",
    date: "",
  });

  // Reference and state for measuring the chart container's width
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800); // Set default width

  // ─── Fetch all vitals on mount ───
  useEffect(() => {
    const fetchVitals = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const tenantId = localStorage.getItem("tenantId");
        const res = await axios.get(
          `${API_BASE}patient-vitals/by-patient?patientCode=${patientCode}`,
          { headers: { Authorization: `Bearer ${token}`, tenantId: tenantId } }
        );
        setData(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch vitals", err);
      } finally {
        setLoadingData(false);
      }
    };
    if (patientCode && type) {
      fetchVitals();
    }
  }, [patientCode, type]);

  // ─── Use ResizeObserver to watch the container's width with fallback ───
  useLayoutEffect(() => {
    if (!chartContainerRef.current) return;

    // Set initial width immediately
    const initialWidth = chartContainerRef.current.offsetWidth;
    if (initialWidth > 0) {
      setChartWidth(initialWidth);
    }

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Use different methods to get width
        let width = 0;
        if (entry.contentRect && entry.contentRect.width > 0) {
          width = entry.contentRect.width;
        } else if (entry.target && entry.target.offsetWidth > 0) {
          width = entry.target.offsetWidth;
        }
        if (width > 0) {
          setChartWidth(width);
        }
      }
    });

    observer.observe(chartContainerRef.current);

    // Fallback: Use window resize event
    const handleResize = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        if (width > 0) {
          setChartWidth(width);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ─── Prepare parsed values array ───
  const parsedValues = data
    .map((entry) => {
      if (type === "bloodPressure") {
        return {
          date: new Date(entry.createdDate),
          systolic: entry.bloodPressureSystolic || 0,
          diastolic: entry.bloodPressureDiastolic || 0,
          originalEntry: entry,
        };
      }

      let value = null;
      switch (type) {
        case "temperature":
          value = entry.temperature || 0;
          break;
        case "oxygenLevel":
          value = entry.oxygenLevel || 0;
          break;
        case "respirationRate":
          value = entry.respirationRate || 0;
          break;
        case "heartRate":
          value = entry.heartRate || 0;
          break;
        case "height":
          value =
            entry.heightFeet > 0
              ? parseFloat(`${entry.heightFeet}.${entry.heightInches || 0}`)
              : entry.heightCm || 0;
          break;
        case "weight":
          value = entry.weightInKg || 0;
          break;
        case "bloodGlucose":
          value = entry.bloodGlucose || 0;
          break;
        default:
          value = 0;
      }

      return {
        date: new Date(entry.createdDate),
        value,
        originalEntry: entry,
      };
    })
    .filter((item) => {
      // Filter out invalid data
      if (type === "bloodPressure") {
        return item.systolic > 0 && item.diastolic > 0;
      }
      return item.value > 0;
    });

  // ─── Tooltip handlers ───
  const handleMouseEnter = (event, dataPoint, valueType = null) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = chartContainerRef.current.getBoundingClientRect();

    let content = "";
    const date = dataPoint.date.toLocaleDateString("en-GB");
    const time = dataPoint.date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (type === "bloodPressure") {
      if (valueType === "systolic") {
        content = `Systolic: ${dataPoint.systolic} mmHg`;
      } else if (valueType === "diastolic") {
        content = `Diastolic: ${dataPoint.diastolic} mmHg`;
      }
    } else {
      const unit = VITAL_LABELS[type]?.match(/\(([^)]+)\)/)?.[1] || "";
      content = `${dataPoint.value}${unit ? " " + unit : ""}`;
    }

    setTooltip({
      show: true,
      x: rect.left - containerRect.left + 10,
      y: rect.top - containerRect.top - 10,
      content,
      date: `${date} ${time}`,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: "", date: "" });
  };

  // ─── Compute SVG path commands ───
  const CHART_HEIGHT = 300;
  const PADDING = 60; // Increased padding for better visibility

  const buildPaths = () => {
    if (parsedValues.length === 0 || chartWidth <= PADDING * 2) {
      return null;
    }

    const n = parsedValues.length;
    const usableWidth = chartWidth - 2 * PADDING;
    const stepX = n > 1 ? usableWidth / (n - 1) : 0;

    if (type === "bloodPressure") {
      const systolics = parsedValues.map((d) => d.systolic);
      const diastolics = parsedValues.map((d) => d.diastolic);
      const maxBP = Math.max(...systolics, ...diastolics);
      const minBP = Math.min(...systolics, ...diastolics);
      const rangeBP = maxBP - minBP || 1;

      // Add some padding to the range
      const paddedMax = maxBP + rangeBP * 0.1;
      const paddedMin = minBP - rangeBP * 0.1;
      const paddedRange = paddedMax - paddedMin;

      const mapYBP = (val) =>
        PADDING +
        ((paddedMax - val) / paddedRange) * (CHART_HEIGHT - 2 * PADDING);

      let pathSys = "";
      let pathDia = "";
      let areaPathSys = `M ${PADDING} ${CHART_HEIGHT - PADDING} `;
      let areaPathDia = `M ${PADDING} ${CHART_HEIGHT - PADDING} `;

      parsedValues.forEach((d, i) => {
        const x = PADDING + i * stepX;
        const ySys = mapYBP(d.systolic);
        const yDia = mapYBP(d.diastolic);

        pathSys += i === 0 ? `M ${x},${ySys}` : ` L ${x},${ySys}`;
        pathDia += i === 0 ? `M ${x},${yDia}` : ` L ${x},${yDia}`;

        areaPathSys += i === 0 ? `L ${x},${ySys}` : ` L ${x},${ySys}`;
        areaPathDia += i === 0 ? `L ${x},${yDia}` : ` L ${x},${yDia}`;

        if (i === n - 1) {
          areaPathSys += ` L ${x},${CHART_HEIGHT - PADDING} Z`;
          areaPathDia += ` L ${x},${CHART_HEIGHT - PADDING} Z`;
        }
      });

      return {
        pathSys,
        pathDia,
        areaPathSys,
        areaPathDia,
        maxBP: paddedMax,
        minBP: paddedMin,
      };
    } else {
      const values = parsedValues.map((d) => d.value);
      const maxV = Math.max(...values);
      const minV = Math.min(...values);
      const rangeV = maxV - minV || 1;

      // Add some padding to the range
      const paddedMax = maxV + rangeV * 0.1;
      const paddedMin = Math.max(0, minV - rangeV * 0.1); // Don't go below 0 for most vitals
      const paddedRange = paddedMax - paddedMin;

      const mapYV = (val) =>
        PADDING +
        ((paddedMax - val) / paddedRange) * (CHART_HEIGHT - 2 * PADDING);

      let path = "";
      let areaPath = `M ${PADDING} ${CHART_HEIGHT - PADDING} `;

      values.forEach((val, i) => {
        const x = PADDING + i * stepX;
        const y = mapYV(val);

        path += i === 0 ? `M ${x},${y}` : ` L ${x},${y}`;
        areaPath += i === 0 ? `L ${x},${y}` : ` L ${x},${y}`;

        if (i === values.length - 1) {
          areaPath += ` L ${x},${CHART_HEIGHT - PADDING} Z`;
        }
      });

      return { path, areaPath, maxV: paddedMax, minV: paddedMin };
    }
  };

  const paths = buildPaths() || {};
  const {
    pathSys,
    pathDia,
    areaPathSys,
    areaPathDia,
    path,
    areaPath,
    maxBP,
    minBP,
    maxV,
    minV,
  } = paths;

  return (
    <OurLayouts>
      <div className="p-6 max-w-7xl mx-auto bg-gradient-to-br from-blue-50 via-white to-green-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#004276] mb-2">
            Vital History
          </h1>
          <div className="flex items-center space-x-2">
            <div className="h-1 w-16 bg-gradient-to-r from-[#004276] to-emerald-500 rounded-full"></div>
            <span className="text-base text-gray-600 font-medium">
              {VITAL_LABELS[type] || type}
            </span>
          </div>
        </div>

        {loadingData ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#004276] mb-4"></div>
              <p className="text-lg text-gray-600">Loading vital data...</p>
            </div>
          </div>
        ) : parsedValues.length === 0 ? (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-lg p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-yellow-800 font-medium">
                  No valid data found for this vital sign.
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  Please check if readings have been recorded for this patient.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Table Container */}
            <div className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden border border-gray-100">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-[#004576] to-[#005c77] px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                    Recorded Values
                  </h2>
                  <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                    <span className="text-white text-sm font-medium">
                      {data.length} readings
                    </span>
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="relative">
                <div className="overflow-x-auto">
                  <div style={{ maxHeight: "300px", overflowY: "auto" }} className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">

                    <table className="min-w-full">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-gradient-to-r from-gray-50 to-blue-50 border-b-2 border-gray-200">
                          <th className="px-6 py-4 text-left text-xs font-bold text-[#004276] uppercase tracking-wider border-r border-gray-200">
                            <div className="flex items-center space-x-2">
                              <span className="w-2 h-2 bg-[#004276] rounded-full"></span>
                              <span>Sl. No</span>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-[#004276] uppercase tracking-wider border-r border-gray-200">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              <span>Date</span>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-[#004276] uppercase tracking-wider border-r border-gray-200">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              <span>Time</span>
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-[#004276] uppercase tracking-wider">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Measurement</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {data.map((entry, idx) => {
                          const dt = new Date(entry.createdDate);
                          const date = dt.toLocaleDateString("en-GB");
                          const time = dt.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          });

                          let measurement = "-";
                          let measurementColor = "text-gray-500";
                          let measurementBg = "";

                          switch (type) {
                            case "temperature":
                              if (entry.temperature) {
                                measurement = `${entry.temperature} °F`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "bloodPressure":
                              if (entry.bloodPressureSystolic && entry.bloodPressureDiastolic) {
                                measurement = `${entry.bloodPressureSystolic}/${entry.bloodPressureDiastolic} mmHg`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "oxygenLevel":
                              if (entry.oxygenLevel) {
                                measurement = `${entry.oxygenLevel} %`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "respirationRate":
                              if (entry.respirationRate) {
                                measurement = `${entry.respirationRate} /min`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "heartRate":
                              if (entry.heartRate) {
                                measurement = `${entry.heartRate} bpm`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "height":
                              if (entry.heightFeet > 0) {
                                measurement = `${entry.heightFeet} ft ${entry.heightInches || 0} in`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              } else if (entry.heightCm) {
                                measurement = `${entry.heightCm} cm`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "weight":
                              if (entry.weightInKg) {
                                measurement = `${entry.weightInKg} kg`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            case "bloodGlucose":
                              if (entry.bloodGlucose) {
                                measurement = `${entry.bloodGlucose} mg/dL`;
                                measurementColor = "text-green-600";
                                measurementBg = "bg-green-50";
                              }
                              break;
                            default:
                              measurement = "-";
                          }

                          return (
                            <tr
                              key={idx}
                              className={`transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-sm ${
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                              }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap border-r border-gray-100">
                                <div className="flex items-center">
                                  <div className="flex items-center justify-center text-black text-sm font-medium">
                                    {idx + 1}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-100">
                                <div className="flex items-center space-x-2">
                                
                                  <span className="font-medium">{date}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 border-r border-gray-100">
                                <div className="flex items-center space-x-1">
                                 
                                  <span>{time}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${measurementBg} ${measurementColor}`}>
                                  <div className={`w-2 h-2 rounded-full mr-2 ${measurementColor.replace('text-', 'bg-')}`}></div>
                                  {measurement}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div
              ref={chartContainerRef}
              className="bg-white rounded-2xl p-8 shadow-xl mb-8 w-full relative border border-gray-100"
              style={{ minHeight: "400px" }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#004276] flex items-center">
                  <svg className="w-6 h-6 mr-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Overall Trends
                </h2>
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-full px-4 py-2 border border-emerald-200">
                  <span className="text-[#004276] font-semibold">
                    {parsedValues.length} readings
                  </span>
                </div>
              </div>

              {/* Tooltip */}
              {tooltip.show && (
                <div
                  className="absolute bg-gray-900 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-10 pointer-events-none border border-gray-700"
                  style={{
                    left: tooltip.x,
                    top: tooltip.y,
                    transform: "translate(-50%, -100%)",
                  }}
                >
                  <div className="font-semibold text-white">{tooltip.content}</div>
                  <div className="text-xs text-gray-300 mt-1">{tooltip.date}</div>
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                    <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}

              {/* SVG Chart */}
              <div className="" style={{ height: `${CHART_HEIGHT}px` }}>
                <svg
                  width="100%"
                  height={CHART_HEIGHT}
                  viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
                  className="rounded-lg"
                >
                  {/* ─── Gradients ─── */}
                  <defs>
                    <linearGradient
                      id="areaGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient
                      id="areaGradientSys"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient
                      id="areaGradientDia"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>

                  {/* ─── Horizontal Grid Lines ─── */}
                  {[0.2, 0.4, 0.6, 0.8].map((frac) => (
                    <line
                      key={frac}
                      x1={PADDING}
                      y1={PADDING + (CHART_HEIGHT - 2 * PADDING) * frac}
                      x2={chartWidth - PADDING}
                      y2={PADDING + (CHART_HEIGHT - 2 * PADDING) * frac}
                      stroke="#E5E7EB"
                      strokeWidth={1}
                      strokeDasharray="2 2"
                    />
                  ))}

                  {/* ─── Y-axis labels ─── */}
                  {type === "bloodPressure" && maxBP && minBP ? (
                    <>
                      {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
                        const value = Math.round(
                          minBP + (maxBP - minBP) * (1 - frac)
                        );
                        const y = PADDING + (CHART_HEIGHT - 2 * PADDING) * frac;
                        return (
                          <text
                            key={frac}
                            x={PADDING - 10}
                            y={y + 4}
                            textAnchor="end"
                            fontSize="12"
                            fill="#6B7280"
                          >
                            {value}
                          </text>
                        );
                      })}
                    </>
                  ) : maxV && minV ? (
                    <>
                      {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
                        const value =
                          Math.round((minV + (maxV - minV) * (1 - frac)) * 10) /
                          10;
                        const y = PADDING + (CHART_HEIGHT - 2 * PADDING) * frac;
                        return (
                          <text
                            key={frac}
                            x={PADDING - 10}
                            y={y + 4}
                            textAnchor="end"
                            fontSize="12"
                            fill="#6B7280"
                          >
                            {value}
                          </text>
                        );
                      })}
                    </>
                  ) : null}

                  {type === "bloodPressure" ? (
                    <>
                      {/* Diastolic area */}
                      {areaPathDia && (
                        <path
                          d={areaPathDia}
                          fill="url(#areaGradientDia)"
                          stroke="none"
                        />
                      )}
                      {/* Systolic area */}
                      {areaPathSys && (
                        <path
                          d={areaPathSys}
                          fill="url(#areaGradientSys)"
                          stroke="none"
                        />
                      )}
                      {/* Diastolic line */}
                      {pathDia && (
                        <path
                          d={pathDia}
                          fill="none"
                          stroke="#1E40AF"
                          strokeWidth={3}
                        />
                      )}
                      {/* Systolic line */}
                      {pathSys && (
                        <path
                          d={pathSys}
                          fill="none"
                          stroke="#DC2626"
                          strokeWidth={3}
                        />
                      )}
                      {/* Data points */}
                      {parsedValues.map((d, i) => {
                        const n = parsedValues.length;
                        const usableWidth = chartWidth - 2 * PADDING;
                        const stepX = n > 1 ? usableWidth / (n - 1) : 0;
                        const x = PADDING + i * stepX;

                        const paddedRange = maxBP - minBP;
                        const yDia =
                          PADDING +
                          ((maxBP - d.diastolic) / paddedRange) *
                            (CHART_HEIGHT - 2 * PADDING);
                        const ySys =
                          PADDING +
                          ((maxBP - d.systolic) / paddedRange) *
                            (CHART_HEIGHT - 2 * PADDING);

                        return (
                          <React.Fragment key={i}>
                            <circle
                              cx={x}
                              cy={yDia}
                              r={5}
                              fill="#1E40AF"
                              stroke="#FFFFFF"
                              strokeWidth={2}
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                handleMouseEnter(e, d, "diastolic")
                              }
                              onMouseLeave={handleMouseLeave}
                            />
                            <circle
                              cx={x}
                              cy={ySys}
                              r={5}
                              fill="#DC2626"
                              stroke="#FFFFFF"
                              strokeWidth={2}
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                handleMouseEnter(e, d, "systolic")
                              }
                              onMouseLeave={handleMouseLeave}
                            />
                          </React.Fragment>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {/* Single‐line vital area fill */}
                      {areaPath && (
                        <path
                          d={areaPath}
                          fill="url(#areaGradient)"
                          stroke="none"
                        />
                      )}
                      {/* Single‐line vital stroke */}
                      {path && (
                        <path
                          d={path}
                          fill="none"
                          stroke="#059669"
                          strokeWidth={3}
                        />
                      )}
                      {/* Data points */}
                      {parsedValues.map((d, i) => {
                        const n = parsedValues.length;
                        const usableWidth = chartWidth - 2 * PADDING;
                        const stepX = n > 1 ? usableWidth / (n - 1) : 0;
                        const x = PADDING + i * stepX;

                        const paddedRange = maxV - minV;
                        const y =
                          PADDING +
                          ((maxV - d.value) / paddedRange) *
                            (CHART_HEIGHT - 2 * PADDING);

                        return (
                          <circle
                            key={i}
                            cx={x}
                            cy={y}
                            r={5}
                            fill="#059669"
                            stroke="#FFFFFF"
                            strokeWidth={2}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={(e) => handleMouseEnter(e, d)}
                            onMouseLeave={handleMouseLeave}
                          />
                        );
                      })}
                    </>
                  )}

                  {/* Legend for blood pressure */}
                  {type === "bloodPressure" && (
                    <g>
                      <rect
                        x={chartWidth - 150}
                        y={20}
                        width={120}
                        height={50}
                        fill="white"
                        stroke="#E5E7EB"
                        strokeWidth={1}
                        rx={4}
                      />
                      <line
                        x1={chartWidth - 140}
                        y1={35}
                        x2={chartWidth - 120}
                        y2={35}
                        stroke="#DC2626"
                        strokeWidth={3}
                      />
                      <text
                        x={chartWidth - 115}
                        y={39}
                        fontSize="12"
                        fill="#374151"
                      >
                        Systolic
                      </text>
                      <line
                        x1={chartWidth - 140}
                        y1={55}
                        x2={chartWidth - 120}
                        y2={55}
                        stroke="#1E40AF"
                        strokeWidth={3}
                      />
                      <text
                        x={chartWidth - 115}
                        y={59}
                        fontSize="12"
                        fill="#374151"
                      >
                        Diastolic
                      </text>
                    </g>
                  )}
                </svg>
              </div>
            </div>

            {/* ──────────── Table Section ──────────── */}
          </>
        )}
      </div>
    </OurLayouts>
  );
}
